import React from 'react';
import { API_BASE_URL } from '../../config/api';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Check, X, Loader2,
  MapPin, Phone, Mail, User, Image, FileText,
  ChevronDown, ChevronUp, Archive, Trash2
} from 'lucide-react';
import { statusColors } from './constants';

export const HandymenTab = ({
  handymen,
  handymanFilter,
  setHandymanFilter,
  expandedHandyman,
  setExpandedHandyman,
  onApprove,
  onReject,
  onArchive,
  onDelete,
  actionLoading,
  searchQuery = '',
}) => {
  const filteredHandymen = handymen.filter(h =>
    handymanFilter === 'all' ? true : h.status === handymanFilter
  );

  return (
    <>
      {/* Status Filter */}
      <div className="mb-4 flex flex-wrap gap-2">
        {['pending', 'approved', 'rejected', 'all'].map((status) => (
          <Button
            key={status}
            variant={handymanFilter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setHandymanFilter(status)}
            className="text-xs md:text-sm capitalize"
          >
            {status}
          </Button>
        ))}
      </div>

      {filteredHandymen.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <User className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-semibold mb-2 font-['Outfit']">{searchQuery ? 'No matching applications' : `No ${handymanFilter} applications`}</h3>
            <p className="text-muted-foreground">{searchQuery ? `No handymen match "${searchQuery}"` : 'Handyman applications will appear here'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredHandymen.map((h) => (
            <HandymanCard
              key={h.id}
              handyman={h}
              expanded={expandedHandyman === h.id}
              onToggle={() => setExpandedHandyman(expandedHandyman === h.id ? null : h.id)}
              onApprove={() => onApprove(h.id)}
              onReject={() => onReject(h.id)}
              onArchive={() => onArchive(h.id)}
              onDelete={() => onDelete(h.id)}
              actionLoading={actionLoading === h.id}
            />
          ))}
        </div>
      )}
    </>
  );
};

const HandymanCard = ({ handyman: h, expanded, onToggle, onApprove, onReject, onArchive, onDelete, actionLoading }) => {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        {/* Header */}
        <div
          className="p-3 md:p-4 cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={onToggle}
        >
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 md:w-6 md:h-6 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-base md:text-lg font-['Outfit'] truncate">{h.name}</h3>
                  <Badge className={`${statusColors[h.status] || statusColors.pending} text-xs`}>
                    {h.status}
                  </Badge>
                </div>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {h.experience_years} yrs exp • {h.services?.length || 0} services
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {h.cv_url && (
                <Badge variant="outline" className="gap-1 text-xs">
                  <FileText className="w-3 h-3" /> CV
                </Badge>
              )}
              {h.work_photos?.length > 0 && (
                <Badge variant="outline" className="gap-1 text-xs">
                  <Image className="w-3 h-3" /> {h.work_photos.length}
                </Badge>
              )}
              {expanded ?
                <ChevronUp className="w-5 h-5 text-muted-foreground" /> :
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              }
            </div>
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="border-t p-3 md:p-4 bg-muted/30">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              {/* Contact & Services */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2 text-xs uppercase tracking-wide text-muted-foreground">Contact Info</h4>
                  <div className="space-y-2 text-sm">
                    <p className="flex items-center gap-2 break-all"><Mail className="w-4 h-4 flex-shrink-0" /> {h.email}</p>
                    <p className="flex items-center gap-2"><Phone className="w-4 h-4 flex-shrink-0" /> {h.phone}</p>
                    <p className="flex items-center gap-2"><MapPin className="w-4 h-4 flex-shrink-0" /> {h.location || h.coverage_areas || 'Not specified'}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2 text-xs uppercase tracking-wide text-muted-foreground">Services Offered</h4>
                  <div className="flex flex-wrap gap-1">
                    {h.services?.map((s) => (
                      <Badge key={s} variant="secondary" className="text-xs capitalize">{s}</Badge>
                    ))}
                  </div>
                </div>

                {h.preferred_work?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2 text-xs uppercase tracking-wide text-muted-foreground">Prefers To Do</h4>
                    <div className="flex flex-wrap gap-1">
                      {h.preferred_work.map((w) => (
                        <Badge key={w} className="text-xs bg-green-100 text-green-800">{w}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {h.certifications && (
                  <div>
                    <h4 className="font-semibold mb-2 text-xs uppercase tracking-wide text-muted-foreground">Certifications</h4>
                    <p className="text-sm">{h.certifications}</p>
                  </div>
                )}

                {h.insurance_details && (
                  <div>
                    <h4 className="font-semibold mb-2 text-xs uppercase tracking-wide text-muted-foreground">Insurance</h4>
                    <p className="text-sm">{h.insurance_details}</p>
                  </div>
                )}

                {h.bio && (
                  <div>
                    <h4 className="font-semibold mb-2 text-xs uppercase tracking-wide text-muted-foreground">About</h4>
                    <p className="text-sm">{h.bio}</p>
                  </div>
                )}
              </div>

              {/* Skills & Portfolio */}
              <div className="space-y-4">
                {h.skill_ratings?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2 text-xs uppercase tracking-wide text-muted-foreground">Skill Self-Assessment</h4>
                    <div className="space-y-2 max-h-48 md:max-h-60 overflow-y-auto pr-2">
                      {h.skill_ratings
                        .sort((a, b) => b.rating - a.rating)
                        .map((skill) => (
                          <div key={skill.skill} className="flex items-center justify-between gap-2">
                            <span className="text-xs md:text-sm truncate flex-1">{skill.skill}</span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <div className="w-16 md:w-24 h-2 bg-muted rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${
                                    skill.rating >= 8 ? 'bg-green-500' :
                                    skill.rating >= 5 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${skill.rating * 10}%` }}
                                />
                              </div>
                              <span className="text-xs md:text-sm font-bold w-5 text-right">{skill.rating}</span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {h.cv_url && (
                  <div>
                    <h4 className="font-semibold mb-2 text-xs uppercase tracking-wide text-muted-foreground">CV / Resume</h4>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        const baseUrl = API_BASE_URL || '';
                        const cvUrl = h.cv_url.startsWith('http') ? h.cv_url : `${baseUrl}${h.cv_url.startsWith('/api') ? h.cv_url : `/api${h.cv_url}`}`;
                        window.open(cvUrl, '_blank', 'noopener,noreferrer');
                      }}
                      className="inline-flex items-center gap-2 text-primary hover:underline text-sm"
                    >
                      <FileText className="w-4 h-4" />
                      View CV
                    </button>
                  </div>
                )}

                {h.work_photos?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2 text-xs uppercase tracking-wide text-muted-foreground">Work Portfolio</h4>
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                      {h.work_photos.map((url, idx) => {
                        const baseUrl = API_BASE_URL || '';
                        let photoUrl = url;
                        if (!url.startsWith('http')) {
                          photoUrl = url.startsWith('/api') ? `${baseUrl}${url}` : `${baseUrl}/api${url}`;
                        }

                        return (
                          <button
                            key={url}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              window.open(photoUrl, '_blank', 'noopener,noreferrer');
                            }}
                            className="aspect-square rounded-lg overflow-hidden hover:opacity-80 transition-opacity bg-muted"
                          >
                            <img
                              src={photoUrl}
                              alt={`Work ${idx + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="10">No image</text></svg>';
                              }}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            {h.status === 'pending' && (
              <div className="flex flex-wrap gap-2 mt-4 md:mt-6 pt-4 border-t">
                <Button
                  className="btn-accent text-sm"
                  onClick={onApprove}
                  disabled={actionLoading}
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                  Approve
                </Button>
                <Button
                  variant="outline"
                  className="text-sm"
                  onClick={onReject}
                  disabled={actionLoading}
                >
                  <X className="w-4 h-4 mr-1" /> Reject
                </Button>
              </div>
            )}

            {/* Archive & Delete - always visible */}
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={onArchive}
                disabled={actionLoading}
                className="text-xs md:text-sm text-muted-foreground"
              >
                <Archive className="w-3 h-3 md:w-4 md:h-4 mr-1" /> Archive
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onDelete}
                disabled={actionLoading}
                className="text-destructive hover:bg-destructive/10 text-xs md:text-sm"
              >
                <Trash2 className="w-3 h-3 md:w-4 md:h-4 mr-1" /> Delete
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
